import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Field,
  Heading,
  Input,
  NativeSelect,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import type { DocumentType } from '@/lib/api';
import { RequiredLabel, RequiredLegend } from '@/components/ui/required-field';

export type ClientFormValues = {
  fullName: string;
  documentType: DocumentType;
  documentNumber: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  accountOwnerSellerId: string;
};

export const EMPTY_CLIENT_FORM: ClientFormValues = {
  fullName: '',
  documentType: 'dpi',
  documentNumber: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  accountOwnerSellerId: '',
};

const DOC_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: 'dpi', label: 'DPI' },
  { value: 'passport', label: 'Pasaporte' },
  { value: 'nit', label: 'NIT' },
  { value: 'other', label: 'Otro' },
];

type Props = {
  initial: ClientFormValues;
  /** Identity fields (documentType/documentNumber) are immutable after creation. */
  lockIdentity?: boolean;
  submitLabel: string;
  submitting: boolean;
  error?: string | null;
  sellers?: { id: string; fullName: string }[];
  onSubmit: (values: ClientFormValues) => void | Promise<void>;
  onCancel: () => void;
};

export function ClientForm({
  initial,
  lockIdentity,
  submitLabel,
  submitting,
  error,
  sellers = [],
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<ClientFormValues>(initial);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await onSubmit(values);
  }

  return (
    <Box
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border"
      rounded="lg"
      p={{ base: '4', md: '6' }}
    >
      <Heading size="md" mb="5">
        Datos del cliente
      </Heading>
      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <Field.Root required>
            <RequiredLabel>Nombre completo</RequiredLabel>
            <Input
              value={values.fullName}
              onChange={(e) => setValues({ ...values, fullName: e.currentTarget.value })}
              placeholder="Juan Pérez"
              maxLength={200}
            />
          </Field.Root>

          <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
            <Field.Root flex={{ base: '1', md: '0 0 200px' }} required disabled={lockIdentity}>
              <RequiredLabel>Tipo de documento</RequiredLabel>
              <NativeSelect.Root disabled={lockIdentity}>
                <NativeSelect.Field
                  value={values.documentType}
                  onChange={(e) =>
                    setValues({ ...values, documentType: e.currentTarget.value as DocumentType })
                  }
                >
                  {DOC_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            <Field.Root flex="1" required disabled={lockIdentity}>
              <RequiredLabel>Número de documento</RequiredLabel>
              <Input
                value={values.documentNumber}
                onChange={(e) =>
                  setValues({ ...values, documentNumber: e.currentTarget.value })
                }
                placeholder="1234567890123"
                disabled={lockIdentity}
                maxLength={50}
              />
              {lockIdentity && (
                <Field.HelperText>
                  El documento no se puede modificar después de crear al cliente.
                </Field.HelperText>
              )}
            </Field.Root>
          </Stack>

          <Stack direction={{ base: 'column', md: 'row' }} gap="4" align="flex-start">
            <Field.Root flex="1">
              <Field.Label>Teléfono</Field.Label>
              <Input
                value={values.phone}
                onChange={(e) => setValues({ ...values, phone: e.currentTarget.value })}
                placeholder="+502 5555 0000"
                maxLength={30}
              />
            </Field.Root>
            <Field.Root flex="1">
              <Field.Label>Correo</Field.Label>
              <Input
                type="email"
                value={values.email}
                onChange={(e) => setValues({ ...values, email: e.currentTarget.value })}
                placeholder="juan@example.com"
                maxLength={200}
              />
            </Field.Root>
          </Stack>

          <Field.Root>
            <Field.Label>Vendedor responsable (opcional)</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={values.accountOwnerSellerId}
                onChange={(e) => setValues({ ...values, accountOwnerSellerId: e.currentTarget.value })}
              >
                <option value="">Sin asignar</option>
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.fullName}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>Dirección</Field.Label>
            <Input
              value={values.address}
              onChange={(e) => setValues({ ...values, address: e.currentTarget.value })}
              placeholder="3 Av. 12-34 Zona 10, Ciudad de Guatemala"
              maxLength={500}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>Notas</Field.Label>
            <Textarea
              value={values.notes}
              onChange={(e) => setValues({ ...values, notes: e.currentTarget.value })}
              placeholder="Notas internas sobre el cliente."
              rows={3}
            />
          </Field.Root>

          {error && (
            <Text color="red.600" fontSize="sm">
              {error}
            </Text>
          )}

          <RequiredLegend />

          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            gap="3"
            justify="flex-end"
          >
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              w={{ base: 'full', md: 'auto' }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              bg="brand.600"
              color="white"
              _hover={{ bg: 'brand.700' }}
              loading={submitting}
              loadingText="Guardando…"
              w={{ base: 'full', md: 'auto' }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
